/**
 * Reads the references each company already holds, so the preview can skip anyone
 * already mapped instead of letting the API store a second value as an alias.
 *
 * There is no bulk read available: `GET /v1/address-book/external-references`
 * requires both `entityType` and `entityId`, and the companies batch endpoint returns
 * names only. So this is one request per company — cheap, concurrent, and it keeps
 * the API unchanged.
 *
 * A company whose lookup keeps failing is fatal rather than assumed-unmapped: writing
 * on the strength of an unknown current state is exactly what the rule exists to
 * prevent. Reads are idempotent, so retrying the preview costs nothing.
 */

export type ExistingLookup = (
  companyUuid: string,
) => Promise<Array<{ referenceType: string; referenceValue: string }>>;

export type CheckOptions = {
  concurrency?: number;
  attempts?: number;
  onProgress?: (done: number, total: number) => void;
  sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_CONCURRENCY = 6;
const DEFAULT_ATTEMPTS = 3;
const BACKOFF_BASE_MS = 200;

export function isTransientLookupError(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return typeof status !== "number" || status >= 500;
}

/**
 * Re-reads a single company immediately before writing to it, closing the gap between
 * the preview snapshot and the operator clicking confirm. Without this, a reference
 * added by someone else in between would be stored as a second, non-canonical alias —
 * the exact outcome the one-per-company rule exists to prevent, because the API only
 * rejects an *exact* duplicate.
 *
 * Throws a 409-shaped error when the company is already mapped, so the caller's
 * existing status mapping reports it as already-mapped with no special casing.
 */
export async function assertStillUnmapped(
  companyUuid: string,
  referenceType: string,
  lookup: ExistingLookup,
): Promise<void> {
  const references = await lookup(companyUuid);
  const held = references
    .filter(reference => reference.referenceType === referenceType)
    .map(reference => reference.referenceValue);

  if (held.length > 0) {
    throw Object.assign(new Error(`Already holds ${held[0]} — added after the preview was taken`), {
      status: 409,
      code: "ALREADY_MAPPED",
    });
  }
}

/**
 * @returns Values of `referenceType` held per company UUID, lowercased keys. A company
 *   with none is present with an empty array, so callers can tell "checked and unmapped"
 *   from "never checked".
 */
export async function checkExisting(
  companyUuids: string[],
  referenceType: string,
  lookup: ExistingLookup,
  options: CheckOptions = {},
): Promise<Map<string, string[]>> {
  const concurrency = Math.max(1, options.concurrency ?? DEFAULT_CONCURRENCY);
  const attempts = Math.max(1, options.attempts ?? DEFAULT_ATTEMPTS);
  const sleep = options.sleep ?? (ms => new Promise(resolve => setTimeout(resolve, ms)));

  const existing = new Map<string, string[]>();
  const unchecked: string[] = [];
  let next = 0;
  let done = 0;

  const worker = async (): Promise<void> => {
    while (next < companyUuids.length) {
      const companyUuid = companyUuids[next] as string;
      next += 1;

      for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
          const references = await lookup(companyUuid);
          existing.set(
            companyUuid.toLowerCase(),
            references
              .filter(reference => reference.referenceType === referenceType)
              .map(reference => reference.referenceValue),
          );
          break;
        } catch (error) {
          if (!isTransientLookupError(error) || attempt === attempts) {
            unchecked.push(companyUuid);
            break;
          }
          await sleep(BACKOFF_BASE_MS * 2 ** (attempt - 1));
        }
      }

      done += 1;
      options.onProgress?.(done, companyUuids.length);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, companyUuids.length) }, () => worker()),
  );

  if (unchecked.length > 0) {
    throw new Error(
      `Could not read existing references for ${unchecked.length} of ${companyUuids.length} ` +
        "companies, so the preview is incomplete. Nothing has been written — try again.",
    );
  }

  return existing;
}
